import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { Fragment, useState } from "react";

import {
  FeatureFlagState,
  FeatureFlagUpdate,
  PermissionBit,
  UserFeatureFlag,
} from "../api";
import { ApiConnector } from "../api/api-connector";
import { FeatureFlags } from "../api/user-feature-flags";
import {
  userFeatureFlagHints,
  userFeatureFlags,
} from "../constants/enum-to-text";
import {
  hasAdminAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../constants/permissions";
import { HelperTooltip } from "./ui-kit/base/helper-tooltip";
import { Checkbox } from "./ui-kit/fields/checkbox";
import { Switch } from "./ui-kit/fields/switch";
import { resolveEnumLabel } from "./ui-kit/fields/utils";

/**
 * Feature flags of one user, as the API serves them - the full registry, merged
 * with its code-side defaults - so a flag added on the backend shows up without
 * a change here, only its label and hint in `enum-to-text` are worth adding.
 *
 * Which user, and how a change is persisted, is the caller's business: a
 * patient's connected user goes through the patient, an arbitrary user through
 * the admin console. Each toggle saves on its own rather than as part of a
 * form, so only the sub-field that changed is ever sent.
 */
export const UserFeatureFlags = observer(function UserFeatureFlags(props: {
  api: ApiConnector;
  flags: FeatureFlags;
  onSave: (flag: UserFeatureFlag, update: FeatureFlagUpdate) => Promise<void>;
}) {
  const { t } = useLingui();
  const [saving, setSaving] = useState<string | null>(null);

  // mirrors PUT /users/{uuid}: flipping the lock itself, or the value of a
  // locked flag, takes staff (monitoring) permissions - or admin, which the
  // API lets set any user's flags outright. A user-editable flag's value needs
  // only the permission each caller has already checked before rendering this
  // - medical-update over the patient, or admin.
  const canEditLock =
    hasAdminAccess(props.api.permissions) ||
    (hasMonitoringAccess(props.api.permissions) &&
      hasPermission(props.api.permissions, PermissionBit.UPDATE_VISIBLE));
  const canEditValue = (state: FeatureFlagState) =>
    canEditLock || state.user_editable !== false;

  const save = async (
    key: string,
    flag: UserFeatureFlag,
    update: FeatureFlagUpdate,
  ) => {
    setSaving(key);
    try {
      await props.onSave(flag, update);
    } finally {
      setSaving(null);
    }
  };

  return (
    /* one grid for all groups, so the two control columns line up across them */
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 gap-y-1 text-sm">
      {canEditLock && (
        <>
          <span />
          <span className="text-muted-foreground w-16 text-center text-xs leading-tight">
            <Trans>User can edit</Trans>
          </span>
          <span />
        </>
      )}

      {groupFlags(props.flags).map((group) => (
        <Fragment key={group.prefix}>
          <div className="text-muted-foreground col-span-3 mt-1 text-xs">
            {resolveEnumLabel(group.label)}
          </div>

          {group.flags.map(([flag, state]) => {
            const editable = canEditValue(state);
            const label = resolveEnumLabel(userFeatureFlags[flag]) || flag;
            const hint = resolveEnumLabel(userFeatureFlagHints[flag]);
            // one tooltip, not two: every flag has a hint, so a locked flag
            // would otherwise render the same icon twice side by side.
            const tooltip = [
              hint,
              editable
                ? ""
                : t`This flag is staff-managed; you cannot change its value.`,
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <Fragment key={flag}>
                <span className="flex items-center gap-1">
                  {label}
                  {tooltip && <HelperTooltip content={tooltip} />}
                </span>

                {/* flex, not an inline box: the checkbox indicator changes
                    the inline baseline when checked, nudging the row by a
                    pixel on every toggle */}
                <span className="flex justify-center">
                  {canEditLock && (
                    <Checkbox
                      ariaLabel={t`User can edit: ${label}`}
                      value={state.user_editable !== false}
                      onChange={(userEditable) => {
                        void save(`${flag}:lock`, flag, {
                          user_editable: userEditable,
                        });
                      }}
                      disabled={saving === `${flag}:lock`}
                    />
                  )}
                </span>

                <span className="flex justify-end">
                  <Switch
                    size="sm"
                    ariaLabel={label}
                    value={state.value === true}
                    onChange={(value) => {
                      void save(`${flag}:value`, flag, { value });
                    }}
                    disabled={!editable || saving === `${flag}:value`}
                  />
                </span>
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
});

/** Flag names carry their consumer as a prefix - group the rows by it. */
const flagGroupLabels: { prefix: string; label: MessageDescriptor }[] = [
  { prefix: "app_", label: msg`Mobile app` },
  { prefix: "fe_", label: msg`Clinic frontend` },
  { prefix: "shared_", label: msg`Shared` },
];

const otherFlagsLabel = msg`Other`;

function groupFlags(flags: FeatureFlags) {
  const entries = Object.entries(flags).sort(([a], [b]) =>
    a.localeCompare(b),
  ) as [UserFeatureFlag, FeatureFlagState][];

  const groups = flagGroupLabels.map(({ prefix, label }) => ({
    prefix,
    label,
    flags: entries.filter(([flag]) => flag.startsWith(prefix)),
  }));

  return [
    ...groups,
    {
      prefix: "",
      label: otherFlagsLabel,
      flags: entries.filter(
        ([flag]) =>
          !flagGroupLabels.some(({ prefix }) => flag.startsWith(prefix)),
      ),
    },
  ].filter((group) => group.flags.length > 0);
}
